import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import "./adminDashboard.css";

const PAGE_SIZE = 10;

export type DashTab = "clients" | "leads" | "onhold" | "archive";

export interface DashCounts {
  clients: number | null;
  leads: number | null;
  onhold: number | null;
  archive: number | null;
}

export interface DashRow {
  id: string;
  primary: string;
  secondary: string;
  badge?: { label: string; tone: "green" | "blue" | "red" | "muted" } | null;
  date: string;
  href: string;
}

interface TabDef {
  key: DashTab;
  label: string;
  meta: string;
}

const TABS: TabDef[] = [
  { key: "clients", label: "Clients", meta: "Active client orgs" },
  { key: "leads", label: "Working", meta: "Live opportunities" },
  { key: "onhold", label: "On Hold", meta: "Waiting, with a date" },
  { key: "archive", label: "Archive", meta: "Junk and wrong fit" },
];

const TABLE_HEADERS: Record<DashTab, [string, string, string, string]> = {
  clients: ["Organization", "Stage", "Status", "Created"],
  leads: ["Organization", "Source", "Status", "Created"],
  onhold: ["Organization", "Waiting on", "Due", "Created"],
  archive: ["Organization", "Reason", "Status", "Archived"],
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
}

function fmtCount(n: number | null): string {
  return n === null ? "—" : String(n);
}

interface AdminDashboardViewProps {
  counts: DashCounts;
  activeTab: DashTab;
  onTabChange: (tab: DashTab) => void;
  rows: DashRow[];
  loading: boolean;
  /** Right-aligned control on the table head, e.g. Add Lead on the Working tab. */
  tableAction?: ReactNode;
}

/**
 * Presentational admin dashboard in the "RC-restyle" design language
 * (cool slate + F2L blue, DM Sans, 8px radii). Pure UI — all data and the
 * active-tab decision come from props, so the same view powers both the real
 * /admin page and the public /preview/dashboard route. The page title lives in
 * the app-shell header, so this view renders straight into the count tabs.
 */
export function AdminDashboardView({
  counts,
  activeTab,
  onTabChange,
  rows,
  loading,
  tableAction,
}: AdminDashboardViewProps) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  // Reset to the first page whenever the tab (and therefore the dataset) changes.
  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  const headers = TABLE_HEADERS[activeTab];

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const pageRows = rows.slice(start, start + PAGE_SIZE);
  const firstShown = rows.length === 0 ? 0 : start + 1;
  const lastShown = Math.min(start + PAGE_SIZE, rows.length);

  return (
    <div className="f2lx-dash">
      <div className="f2lx-tabs">
        {TABS.map((tab) => {
          const on = tab.key === activeTab;
          return (
            <button
              key={tab.key}
              type="button"
              className={"f2lx-tab" + (on ? " on" : "")}
              aria-pressed={on}
              onClick={() => onTabChange(tab.key)}
            >
              <div className="f2lx-tab-label">{tab.label}</div>
              <div className="f2lx-tab-count">{fmtCount(counts[tab.key])}</div>
              <div className="f2lx-tab-meta">{tab.meta}</div>
            </button>
          );
        })}
      </div>

      <div className="f2lx-card" style={{ overflow: "hidden" }}>
        <div className="f2lx-table-head">
          <span className="f2lx-table-title">
            {TABS.find((t) => t.key === activeTab)?.label}
          </span>
          {/* The row count was noise — the tab above already carries it. This is
              where the action for the current tab goes instead. */}
          {tableAction}
        </div>
        <div className="f2lx-table-wrap">
        <table className="f2lx-table">
          <colgroup>
            <col style={{ width: "40%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "20%" }} />
          </colgroup>
          <thead>
            <tr>
              <th className="f2lx-th">{headers[0]}</th>
              <th className="f2lx-th">{headers[1]}</th>
              <th className="f2lx-th">{headers[2]}</th>
              <th className="f2lx-th" style={{ textAlign: "right" }}>{headers[3]}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="f2lx-empty" colSpan={4}>Loading…</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="f2lx-empty" colSpan={4}>Nothing here yet.</td>
              </tr>
            ) : (
              pageRows.map((row) => (
                <tr
                  key={row.id}
                  className="f2lx-row"
                  tabIndex={0}
                  role="button"
                  onClick={() => navigate(row.href)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(row.href);
                    }
                  }}
                >
                  <td className="f2lx-td">
                    <span className="f2lx-name-wrap">
                      <span className="f2lx-avatar">{initials(row.primary)}</span>
                      <span className="f2lx-name" style={{ fontWeight: 500 }}>{row.primary}</span>
                    </span>
                  </td>
                  <td className="f2lx-td is-secondary" style={{ color: "var(--x-ink-muted)" }}>{row.secondary}</td>
                  <td className="f2lx-td">
                    {row.badge ? (
                      <span className={"f2lx-pill " + row.badge.tone}>{row.badge.label}</span>
                    ) : (
                      <span style={{ color: "var(--x-ink-subtle)" }}>—</span>
                    )}
                  </td>
                  <td className="f2lx-td" style={{ textAlign: "right", color: "var(--x-ink-subtle)" }}>{row.date}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
        {!loading && rows.length > 0 ? (
          <div className="f2lx-pager">
            <span className="f2lx-pager-info">
              Showing {firstShown}–{lastShown} of {rows.length}
            </span>
            {totalPages > 1 ? (
              <div className="f2lx-pager-nav">
                <button
                  type="button"
                  className="f2lx-pager-btn"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <span className="f2lx-pager-page">
                  Page {safePage} of {totalPages}
                </span>
                <button
                  type="button"
                  className="f2lx-pager-btn"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
